import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errorText, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthLayout } from "@/components/brand/AuthLayout";
import { FormError } from "@/components/brand/Field";

export function UpgradePanel() {
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/payments/plan-summary").then((r) => setSummary(r.data)).catch((e) => setErr(errorText(e))); }, []);
  if (!summary) return null;
  const go = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { setErr(errorText(e)); setBusy(false); }
  };
  return (
    <div className="rounded border p-4 mt-3" style={{ borderColor: "var(--blue)", background: "var(--blue-soft)" }} data-testid="upgrade-panel">
      <p className="eyebrow" style={{ color: "var(--blue-deep)" }}>Plan summary</p>
      <p className="text-sm mt-1"><strong>{summary.plan.name}</strong> · <span className="num">{money(summary.price_cents, summary.currency)}/month</span> · {summary.plan.tagline}</p>
      <ul className="text-xs mt-2 list-disc pl-4" style={{ color: "var(--ink-soft)" }}>
        <li>Up to {summary.plan.sources_max} mailboxes, {summary.plan.scan_months}-month scan then weekly light scans</li>
        <li>{summary.plan.uploads_per_month} uploads a month, unlimited confirmations</li>
        <li>Renewal alerts, monthly benchmark refresh, draft-and-confirm cancellation requests</li>
      </ul>
      <p className="text-xs mt-2" style={{ color: "var(--muted-text)" }}>{summary.grandfather_note} Card details are entered on Stripe's hosted page and never touch our servers. Your plan changes only after payment succeeds.</p>
      {summary.stripe_mode === "test" && <p className="text-xs mt-1 font-semibold" style={{ color: "var(--amber)" }}>Test mode. Use card 4242 4242 4242 4242.</p>}
      <FormError message={err} testId="upgrade-error" />
      <button className="ts-btn ts-btn-primary mt-3" disabled={busy} onClick={go} data-testid="upgrade-button">{busy ? "Opening secure payment…" : `Continue to payment · ${money(summary.price_cents, summary.currency)}/mo`}</button>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return setState("missing");
    let tries = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); refresh(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (_) { /* keep polling */ }
      if (++tries < 10) setTimeout(tick, 2000); else setState("timeout");
    };
    tick();
  }, [params, refresh]);
  return (
    <AuthLayout title={state === "paid" ? "You're on Core Plan" : state === "checking" ? "Confirming your payment…" : "Payment not confirmed"}
      subtitle={state === "paid" ? "Your tier has changed now that payment succeeded. Renewals, alerts and unlimited confirmations are unlocked." : state === "checking" ? "This takes a moment. We only change your plan after Stripe confirms." : state === "timeout" ? "We couldn't confirm yet. If you were charged, your plan will update automatically; check back shortly." : "Nothing was changed on your account."}
      footer={<Link className="ts-link" to="/app" data-testid="payment-continue">Go to your workspace</Link>}>
      <p className="text-sm num" data-testid="payment-state" data-state={state}>{state === "checking" ? "Checking…" : state}</p>
    </AuthLayout>
  );
}

export function PaymentCancel() {
  return (
    <AuthLayout title="Payment cancelled" subtitle="No charge was made and your plan is unchanged." footer={<Link className="ts-link" to="/app/settings" data-testid="payment-back">Back to Settings</Link>}>
      <p className="text-sm" data-testid="payment-cancel-page">You can upgrade any time from Settings.</p>
    </AuthLayout>
  );
}
